import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { LEGAL_META, PRIVACY_POLICY_SECTIONS, TERMS_OF_SERVICE_SECTIONS } from './legalContent';
import './LegalDocumentModal.css';

const DOC_META = {
    privacy: { label: 'Privacy Policy', sections: PRIVACY_POLICY_SECTIONS },
    terms: { label: 'Terms of Service', sections: TERMS_OF_SERVICE_SECTIONS },
};

/* Renders **bold** spans inside a plain string as <strong>. */
function renderInline(text) {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
    );
}

function ListItemContent({ item }) {
    if (typeof item === 'string') return <>{renderInline(item)}</>;
    return (
        <>
            <strong>{item.lead} </strong>
            {renderInline(item.text)}
        </>
    );
}

function Block({ block }) {
    if (block.type === 'p') return <p className={`ldm-p${block.muted ? ' ldm-p--muted' : ''}`}>{renderInline(block.text)}</p>;
    if (block.type === 'ul') {
        return (
            <ul className="ldm-list">
                {block.items.map((item, i) => <li key={i}><ListItemContent item={item} /></li>)}
            </ul>
        );
    }
    if (block.type === 'ol') {
        return (
            <ol className="ldm-list ldm-list--ordered">
                {block.items.map((item, i) => <li key={i}><ListItemContent item={item} /></li>)}
            </ol>
        );
    }
    if (block.type === 'contact') {
        return (
            <div className="ldm-contact">
                {block.lines.map((line) => (
                    <div key={line.label} className="ldm-contact-row">
                        <span className="ldm-contact-label">{line.label}</span>
                        {line.href ? (
                            <a className="ldm-contact-value ldm-contact-value--link" href={line.href}>{line.value}</a>
                        ) : (
                            <span className="ldm-contact-value">{line.value}</span>
                        )}
                    </div>
                ))}
            </div>
        );
    }
    if (block.type === 'table') {
        return (
            <div className="ldm-table-wrap">
                <table className="ldm-table">
                    <thead>
                        <tr>{block.head.map((h, i) => <th key={i}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {block.rows.map((row, i) => (
                            <tr key={i}>{row.map((cell, j) => <td key={j}>{renderInline(cell)}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    return null;
}

export default function LegalDocumentModal({ doc, initialSection, onClose }) {
    const meta = DOC_META[doc] || DOC_META.privacy;
    const [activeSection, setActiveSection] = useState(initialSection || meta.sections[0].id);
    const bodyRef = useRef(null);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [handleKeyDown]);

    // Jump straight to the requested section on open — no animation, it should
    // already be there when the sheet appears rather than visibly scrolling into place.
    useEffect(() => {
        if (initialSection) {
            document.getElementById(`ldm-${initialSection}`)?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Track which section is in view so the TOC can highlight it.
    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return;
        const onScroll = () => {
            const bodyTop = body.getBoundingClientRect().top;
            let current = meta.sections[0].id;
            for (const section of meta.sections) {
                const el = document.getElementById(`ldm-${section.id}`);
                if (el && el.getBoundingClientRect().top - bodyTop < 80) current = section.id;
            }
            setActiveSection(current);
        };
        body.addEventListener('scroll', onScroll, { passive: true });
        return () => body.removeEventListener('scroll', onScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc]);

    const scrollToSection = (id) => {
        document.getElementById(`ldm-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="ldm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="ldm-sheet" role="dialog" aria-modal="true" aria-labelledby="ldm-title">
                <div className="ldm-header">
                    <div className="ldm-header-text">
                        <h2 className="ldm-title" id="ldm-title">{meta.label}</h2>
                        <span className="ldm-meta">Version {LEGAL_META.version} &middot; Effective {LEGAL_META.effectiveDate}</span>
                    </div>
                    <button className="ldm-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                <div className="ldm-content">
                    <nav className="ldm-toc" aria-label="Sections">
                        {meta.sections.map((section) => (
                            <button
                                key={section.id}
                                className={`ldm-toc-link${activeSection === section.id ? ' ldm-toc-link--active' : ''}`}
                                onClick={() => scrollToSection(section.id)}
                            >
                                {section.title}
                            </button>
                        ))}
                    </nav>

                    <div className="ldm-body" ref={bodyRef}>
                        {meta.sections.map((section) => (
                            <section key={section.id} id={`ldm-${section.id}`} className="ldm-section">
                                <h3 className="ldm-section-title">{section.title}</h3>
                                {section.blocks.map((block, i) => <Block key={i} block={block} />)}
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
